<script>
  /**
   * Password (and, when enabled, magic-link) sign-in against chassis core
   * /auth/*. Structure follows plugins/augurly/svelte/src/routes/Login.svelte.
   *
   * Magic link is gated by MAGIC_LINK_ENABLED — the portal sends no email yet,
   * so the flow would dead-end. See $lib/features.js for the full note.
   */
  import { useRouter } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { MAGIC_LINK_ENABLED } from '$lib/features.js';
  import { authStore } from '../stores/authStore.svelte.js';

  const router = useRouter();

  let email = $state('');
  let password = $state('');
  let useMagicLink = $state(false);
  let magicLinkSent = $state(false);
  let loading = $state(false);
  let error = $state(null);

  const validEmail = $derived(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  const canSubmit = $derived(validEmail && (useMagicLink || password.length > 0) && !loading);

  function redirectTarget() {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect');
  }

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    loading = true;
    error = null;

    try {
      if (MAGIC_LINK_ENABLED && useMagicLink) {
        await api.requestMagicLink(email);
        magicLinkSent = true;
        return;
      }

      const result = await api.login(email, password);
      if (!result?.access_token || !result?.user) {
        throw new Error('Sign-in did not return a session');
      }
      authStore.setAuth(result.user, result.access_token);

      // Portal membership is separate from having a chassis account: a valid
      // login with no membership must not land on an empty dashboard with no
      // explanation.
      try {
        await authStore.loadContext();
      } catch (contextError) {
        if (contextError.status === 403) {
          authStore.clearAuth();
          // A deactivated tenant gets its own message from the server, so the
          // person knows their login is fine and access was suspended.
          error = /suspended/i.test(contextError.message || '')
            ? contextError.message
            : 'This account does not have access to the portal yet. '
              + 'Ask your property manager for an invitation.';
          return;
        }
        throw contextError;
      }

      const target = redirectTarget();
      if (target) window.location.href = target;
      else router.push({ name: 'dashboard' });
    } catch (e) {
      error = e.message || 'Sign-in failed';
    } finally {
      loading = false;
    }
  }
</script>

<div class="flex min-h-screen items-center justify-center px-4">
  <div class="w-full max-w-sm">
    <div class="mb-8 text-center">
      <i class="fas fa-house-chimney text-2xl" style="color: var(--p-brand)" aria-hidden="true"></i>
      <h1 class="mt-3 text-lg font-semibold">Sign in to STRAO Portal</h1>
    </div>

    {#if magicLinkSent}
      <div class="p-card p-6 text-center">
        <i class="fas fa-envelope-open-text text-xl" style="color: var(--p-brand)" aria-hidden="true"></i>
        <h2 class="mt-3 text-sm font-semibold">Check your inbox</h2>
        <p class="mt-2 text-sm p-muted">
          We sent a sign-in link to <strong>{email}</strong>. It expires in 15 minutes.
        </p>
        <button type="button" class="p-btn p-btn-quiet mt-5"
                onclick={() => { magicLinkSent = false; useMagicLink = false; }}>
          Back to sign in
        </button>
      </div>
    {:else}
      <form class="p-card space-y-4 p-6" onsubmit={submit}>
        {#if error}
          <p class="rounded-lg px-3 py-2 text-sm"
             style="background-color: var(--p-danger-bg); color: var(--p-danger)" role="alert">
            {error}
          </p>
        {/if}

        <div>
          <label for="email" class="text-xs font-medium p-muted">Email</label>
          <input id="email" class="p-input mt-1" type="email" bind:value={email}
                 autocomplete="email" required />
        </div>

        {#if !useMagicLink}
          <div>
            <label for="password" class="text-xs font-medium p-muted">Password</label>
            <input id="password" class="p-input mt-1" type="password" bind:value={password}
                   autocomplete="current-password" />
          </div>
        {/if}

        <button type="submit" class="p-btn p-btn-primary w-full justify-center" disabled={!canSubmit}>
          {#if loading}
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
          {/if}
          {useMagicLink ? 'Send sign-in link' : 'Sign in'}
        </button>

        {#if MAGIC_LINK_ENABLED}
          <button type="button" class="w-full text-center text-xs p-muted hover:underline"
                  onclick={() => { useMagicLink = !useMagicLink; error = null; }}>
            {useMagicLink ? 'Use a password instead' : 'Email me a sign-in link instead'}
          </button>
        {/if}
      </form>
    {/if}
  </div>
</div>
